/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useRef, useEffect, useState } from 'react';

// Mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import { useForm, isNotEmpty } from '@mantine/form';
import { randomId } from '@mantine/hooks';

// Styles
import classes from './AddProduction.module.css';

// Components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';

// Components : Steps
import { FormAddProduction } from './components/FormAddProduction';

// Store
import useStore from '@store/index';
import { useTranslation } from 'react-i18next';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddProduction = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);

	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
		productionsAddType,
		setProductionsDefaultSelectedFields,
	} = useStore();

	const { t } = useTranslation();

	const IS_FROM_FILE_ADD_PRODUCTION =
		productionsAddType !== 'manually';

	// Validators
	const form = useForm({
		initialValues: {
			name: '',
			brand: '',
			tag: '',
			quantity: 0,
			startDate: null,
			endDate: null,
			producer: null,
			project: null,
			site: null,
			meterPoint: null,
			unitProduction: null,
			productions: [
				{
					id: randomId(),
					quantity: 0,
					startDate: null,
				},
			],
		},
		validate: {
			producer: isNotEmpty(
				t('errors.producerIsRequired')
			),
			project: isNotEmpty(t('errors.projectIsRequired')),
			site: isNotEmpty(t('errors.siteIsRequired')),
			meterPoint: isNotEmpty(
				t('errors.meterpointIsRequired')
			),
			unitProduction: isNotEmpty(
				t('global.unitRequired')
			),
			name: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value.length < 2
					? t('errors.nameMustHaveAtLeast2Letters')
					: null,
			brand: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value.length == 0
					? t('errors.brandIsRequired')
					: null,
			tag: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value.length == 0
					? t('errors.tagIsRequired')
					: null,

			quantity: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value == 0
					? t('errors.quantityCannotBeZero')
					: null,
			startDate: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value == null
					? t('errors.startDateIsRequired')
					: null,

			endDate: (value) =>
				IS_FROM_FILE_ADD_PRODUCTION && value == null
					? t('errors.endDateIsRequired')
					: null,

			productions: {
				quantity: (value) =>
					!IS_FROM_FILE_ADD_PRODUCTION && value == 0
						? t('errors.quantityCannotBeZero')
						: null,
				startDate: (value) =>
					!IS_FROM_FILE_ADD_PRODUCTION && value == null
						? t('errors.startDateIsRequired')
						: null,
			},
		},
	});

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						form.reset();
						setProductionsDefaultSelectedFields([]);
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={t(
						'productions.backToAllProductionsSure'
					)}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t('productions.backToAllProductions')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<FormAddProduction
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									form.reset();
								}}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />

					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
